import { useCallback, useEffect, useState } from 'react';
import { displaySrc, type ArtworkPiece } from '../lib/artwork';
import { ImageLightbox } from './ImageLightbox';

const ROTATE_MS = 10_000;

export function HeroCarousel({ slides }: { slides: ArtworkPiece[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const count = slides.length;
  const current = slides[index] ?? slides[0];

  const go = useCallback(
    (next: number) => {
      if (count <= 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [count, paused, index]);

  if (!current) {
    return <div className="empty-state">Add a hero image in Studio</div>;
  }

  return (
    <div
      className="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <button
        type="button"
        className="hero-figure hero-figure-button"
        onClick={() => setLightbox(true)}
        aria-label={`View larger: ${current.title}`}
      >
        {slides.map((slide, i) => (
          <img
            key={slide.id}
            src={displaySrc(slide, 'full')}
            alt={slide.title}
            className={`hero-carousel-slide${i === index ? ' is-active' : ''}`}
            fetchPriority={i === 0 ? 'high' : 'low'}
            decoding="async"
            width={1600}
            height={1100}
            aria-hidden={i !== index}
          />
        ))}
      </button>

      <p className="hero-art-credit">
        {current.title}
        {current.medium ? ` · ${current.medium}` : ''}
      </p>

      {count > 1 ? (
        <div className="hero-carousel-dots" role="tablist" aria-label="Hero artwork">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Show ${slide.title}`}
              className={`hero-carousel-dot${i === index ? ' is-active' : ''}`}
              onClick={() => go(i)}
            />
          ))}
        </div>
      ) : null}

      <ImageLightbox
        open={lightbox}
        src={displaySrc(current, 'full')}
        alt={current.title}
        title={current.title}
        onClose={() => setLightbox(false)}
      />
    </div>
  );
}
